"use client";

import { useEffect, useSyncExternalStore } from "react";
import useProduct from "@core/helpers/client/hooks/useProduct";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { useLanguageContext } from "@core/helpers/client/contexts/LanguagesContext";
import { get_cookie_client } from "@core/helpers/client/cookie";
import { parse_local_cart } from "@core/helpers/local-cart";
import { get_cart_query } from "@core/services/auth";
import { cart_row_key } from "@core/helpers/cart-rows";

/**
 * فیلدهای پیش‌فروشِ سبد.
 *
 * سبد را هسته می‌خواند (AuthContext / useProduct) و کوئری‌اش برای ژومنت قابل
 * تغییر نیست (AuthContext گزینه‌ای برای فیلدهای سبد ندارد). پس این هوک با هر تغییرِ سبد یک
 * `getCartDetails`ِ کوچک و فقط با فیلدهای پیش‌فروش می‌زند و نتیجه را بین همه‌ی
 * کامپوننت‌ها (ردیف‌های سبد، چک‌اوت) به اشتراک می‌گذارد.
 *
 * اگر درخواست خطا بدهد (مثلاً بک‌اند هنوز فیلدها را ندارد) نتیجه خالی می‌ماند و
 * سبد مثل قبل کار می‌کند.
 */

const PRESALE_CART_MOCKUP = `
  {
    products {
      product_id
      bundle_id
      mix_variant_keys
      price_key
      is_presale
      presale_lead_hours
      presale_ready_at
      product {
        mix_variant {
          keys
          presale {
            available
            remaining
            lead_hours
          }
        }
      }
    }
    total_prices {
      has_presale
      presale_lead_hours
    }
  }
`;

const EMPTY = Object.freeze({
  rows: Object.freeze({}),
  total: Object.freeze({ has_presale: false, presale_lead_hours: 0 }),
});

const store = {
  state: EMPTY,
  signature: null,
  request_id: 0,
  listeners: new Set(),
};

const set_state = (state) => {
  store.state = state;
  store.listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  store.listeners.add(listener);
  return () => store.listeners.delete(listener);
};

const cart_signature = (cart, is_auth) => {
  const products = cart?.products;
  if (!products?.length) return "";
  return `${is_auth ? "u" : "g"}:${products
    .map((row) => `${cart_row_key(row)}x${row?.count}`)
    .join(",")}`;
};

const load = async (is_auth, lang) => {
  const request_id = ++store.request_id;
  // same language as the cart AuthContext reads; a broken `local-cart` cookie reads as an empty cart (core)
  const variables = { lang };
  if (!is_auth) variables.local_cart = { products: parse_local_cart(get_cookie_client("local-cart")) };

  const result = await get_cart_query(variables, {
    custom_cart_mockup: PRESALE_CART_MOCKUP,
    fetchPolicy: "network-only",
  });

  // پاسخ قدیمی‌تر از آخرین درخواست دور ریخته می‌شود
  if (request_id !== store.request_id) return;
  if (!result?.status || !result?.data) {
    set_state(EMPTY);
    return;
  }

  const rows = {};
  result.data.products?.forEach((row) => {
    const variant = row?.product?.mix_variant?.find(
      (mx) => mx?.keys?.toString() === row?.mix_variant_keys?.toString(),
    ) || row?.product?.mix_variant?.[0];
    // سرور برای سبد کاربر و سبد محلیِ مهمان هر دو `is_presale` را تشخیص می‌دهد
    rows[cart_row_key(row)] = {
      is_presale: row?.is_presale === true,
      lead_hours: row?.presale_lead_hours || 0,
      ready_at: row?.presale_ready_at || null,
      remaining: variant?.presale?.remaining,
    };
  });

  set_state({
    rows,
    total: {
      has_presale: result.data.total_prices?.has_presale === true,
      presale_lead_hours: result.data.total_prices?.presale_lead_hours || 0,
    },
  });
};

/** `{ rows: { [cart_row_key]: { is_presale, lead_hours, ready_at, remaining } }, total: { has_presale, presale_lead_hours } }` */
const usePresaleCart = () => {
  const { cart, is_auth } = useAuthContext();
  const lang = useLanguageContext()?.language || "fa";
  const cart_key = cart_signature(cart, is_auth);
  const signature = cart_key ? `${lang}|${cart_key}` : "";

  useEffect(() => {
    if (signature === store.signature) return;
    store.signature = signature;
    if (!signature) {
      store.request_id++;
      set_state(EMPTY);
      return;
    }
    load(is_auth, lang);
  }, [signature, is_auth, lang]);

  return useSyncExternalStore(
    subscribe,
    () => store.state,
    () => EMPTY,
  );
};

export default usePresaleCart;

/**
 * `check_if_all_items_exist` و `out_stock_products_in_cart` هسته را برای
 * پیش‌فروش درست می‌کند.
 *
 * هسته موجودی ردیف را از `details.count` می‌خواند و ردیف پیش‌فروش (موجودیِ صفر)
 * را «ناموجود» حساب می‌کند؛ نتیجه این بود که خلاصه‌ی سبد کلاً مخفی می‌شد و
 * پیام «از سبد حذف می‌شود» ردیف پیش‌فروش را هم فهرست می‌کرد. اینجا ردیف‌های
 * پیش‌فروش از آن دو کنار گذاشته می‌شوند.
 */
export const usePresaleCartStock = () => {
  const { check_if_all_items_exist, out_stock_products_in_cart } = useProduct();
  const { rows } = usePresaleCart();
  const is_presale_row = (item) => rows[cart_row_key(item)]?.is_presale === true;
  const { cart } = useAuthContext();

  return {
    check_if_all_items_exist:
      check_if_all_items_exist || !!cart?.products?.some(is_presale_row),
    out_stock_products_in_cart: (out_stock_products_in_cart || []).filter(
      (item) => !is_presale_row(item),
    ),
  };
};
